import Content from '@/components/Content';
import Link from '@/components/Link';
import SignInEnrollStep from '@/components/Mfa/SignInEnrollStep';
import SignInMfaStep from '@/components/Mfa/SignInMfaStep';
import RequestResetPasswordDialog from '@/components/RequestResetPasswordDialog';
import WithoutUserFrame from '@/components/WithoutUserFrame';
import {
  MfaMethod,
  useAuthenticateMutation,
  ViewerDocument,
} from '@/generated/graphql';
import { useLayoutStyles } from '@/hooks/styles';
import {
  graphQLErrorCode,
  graphQLErrorExtensions,
  RATE_LIMITED_CODE,
  rateLimitMessage,
} from '@/utils/apollo-errors';
import { MFA_ERROR_CODES } from '@/utils/mfa-errors';
import { safeReturnUrl } from '@/utils/safeReturnUrl';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Link as MuiLink,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useApolloClient } from '@apollo/client';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import * as yup from 'yup';

const LoginSchema = yup.object().shape({
  email: yup.string().required('Required'),
  password: yup.string().required('Required'),
});

interface LoginFields {
  email: string;
  password: string;
}

const initialValues: LoginFields = {
  email: '',
  password: '',
};

export default function Login(): JSX.Element {
  const layoutClasses = useLayoutStyles();
  const router = useRouter();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [authenticate] = useAuthenticateMutation();
  const [reqeustOpen, setRequestOpen] = useState(false);
  // Set once the password is right and the account needs a second factor.
  const [mfa, setMfa] = useState<{
    challengeId?: string;
    methods?: MfaMethod[];
    trustDeviceAllowed?: boolean;
  } | null>(null);
  // An OAuth client asked for a new sign-in (reauth=1), maybe with the
  // second step (mfa=1). The page stays even though the browser is signed
  // in, until a sign-in here finishes.
  const reauth = router.query.reauth === '1';
  const requireSecondStep = router.query.mfa === '1';
  const [signedInHere, setSignedInHere] = useState(false);
  // Set when the account requires MFA after grace: the session may only
  // enroll.
  const [enroll, setEnroll] = useState(false);
  const [lastEmail, setLastEmail] = useState('');
  const apolloClient = useApolloClient();

  const apiOrigin = (() => {
    try {
      return new URL(process.env.NEXT_PUBLIC_API_URL ?? '').origin;
    } catch {
      return '';
    }
  })();
  const redirectUrl = safeReturnUrl(
    router.query.returnUrl as string | undefined,
    apiOrigin,
  );

  const handleRequestPasswordReset = () => {
    setRequestOpen(true);
  };

  async function handleSubmit({ email, password }: LoginFields) {
    setSubmitting(true);
    setLastEmail(email);

    try {
      const { data } = await authenticate({
        variables: {
          email,
          password,
          ...(requireSecondStep && { ignoreTrustedDevice: true }),
        },
        update: (cache) => {
          cache.evict({ fieldName: 'viewer', broadcast: false });
        },
      });

      const userId = data?.authenticate.user?.id;
      setError(userId ? '' : 'Invalid credentials');
      setSignedInHere(Boolean(userId));
    } catch (err) {
      const code = graphQLErrorCode(err);

      if (code === MFA_ERROR_CODES.required) {
        const { challengeId, methods, trustDeviceAllowed } =
          graphQLErrorExtensions(err);
        setError('');
        setMfa({
          challengeId:
            typeof challengeId === 'string' ? challengeId : undefined,
          methods: Array.isArray(methods)
            ? (methods as MfaMethod[])
            : undefined,
          trustDeviceAllowed: trustDeviceAllowed === true,
        });
      } else if (code === MFA_ERROR_CODES.enrollmentRequired) {
        setError('');
        setEnroll(true);
      } else if (code === RATE_LIMITED_CODE) {
        setError(rateLimitMessage(err));
      } else {
        setError('Invalid credentials');
      }
    }

    setSubmitting(false);
  }

  function restartSignIn(message = '') {
    setMfa(null);
    setEnroll(false);
    setError(message);
  }

  // Enrolled: the session is a full one now. WithoutUserFrame sees the
  // viewer and follows returnUrl.
  function finishEnrollment() {
    setSignedInHere(true);
    apolloClient.refetchQueries({ include: [ViewerDocument] });
  }

  return (
    <WithoutUserFrame
      redirectUrl={redirectUrl}
      holdRedirect={reauth && !signedInHere}
    >
      <Content title="Sign in" maxWidth="sm" padtop>
        {enroll && (
          <SignInEnrollStep
            email={lastEmail}
            onDone={finishEnrollment}
            onRestart={restartSignIn}
          />
        )}
        {!enroll && mfa && (
          <SignInMfaStep
            challengeId={mfa.challengeId}
            methods={mfa.methods}
            email={lastEmail}
            trustDeviceAllowed={mfa.trustDeviceAllowed && !requireSecondStep}
            onRestart={restartSignIn}
            onVerified={() => setSignedInHere(true)}
          />
        )}
        {!enroll && !mfa && (
          <>
            {reauth && !error && (
              <Box mb={2}>
                <Alert severity="info" variant="outlined">
                  An app you are connecting asked you to sign in again to
                  confirm it’s you.
                </Alert>
              </Box>
            )}
            {Boolean(error) && (
              <Box mb={2}>
                <Alert severity="error" variant="outlined">
                  {error}
                </Alert>
              </Box>
            )}
            <Card className={layoutClasses.card} variant="outlined">
              <div className={layoutClasses.progress}>
                {submitting && <LinearProgress />}
              </div>
              <CardHeader title="Sign in" />
              <CardContent>
                <Formik
                  initialValues={{ ...initialValues, email: lastEmail }}
                  validationSchema={LoginSchema}
                  onSubmit={handleSubmit}
                >
                  <Form>
                    <Box mb={2}>
                      <Field
                        component={TextField}
                        name="email"
                        type="email"
                        label="Email"
                        variant="outlined"
                        fullWidth
                      />
                    </Box>
                    <Box mb={2}>
                      <Field
                        component={TextField}
                        type="password"
                        label="Password"
                        name="password"
                        variant="outlined"
                        fullWidth
                        // Back from the code step: the email is kept.
                        autoFocus={Boolean(lastEmail)}
                      />
                    </Box>
                    <Box display="flex">
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={submitting}
                        type="submit"
                      >
                        Sign in
                      </Button>
                      <Box flexGrow={1} />
                      <Button
                        variant="text"
                        onClick={handleRequestPasswordReset}
                      >
                        Reset password
                      </Button>
                    </Box>
                  </Form>
                </Formik>
              </CardContent>
            </Card>
          </>
        )}
        <Box p={3} textAlign="center">
          {mfa || enroll ? (
            <MuiLink
              component="button"
              variant="body1"
              color="inherit"
              onClick={() => restartSignIn()}
            >
              Back to sign in
            </MuiLink>
          ) : (
            <Link href="/register" color="inherit">
              Register new user
            </Link>
          )}
        </Box>
      </Content>
      <RequestResetPasswordDialog
        open={reqeustOpen}
        onClose={() => setRequestOpen(false)}
      />
    </WithoutUserFrame>
  );
}
